import React, {useRef, useState, useEffect} from 'react';
import {
    Play,
    Pause,
    Volume2,
    VolumeX,
    Maximize,
    Minimize,
    RotateCcw,
    Clock,
    Cast,
    Loader,
    AlertTriangle,
    Star,
    X,
    PictureInPicture
} from 'lucide-react';
import { useNotification } from './components/NotificationContext';
import Notification from './components/Notification';
import MovieRating from './components/MovieRating';
import AddToPlaylistButton from './components/AddToPlaylistButton';
import ReportIssueModal from './ReportIssueModal';
import NextEpisodeModal from './NextEpisodeModal';
import { findNextEpisode } from './utils/episodeUtils';
//import useDiscordRichPresence from './hooks/useDiscordRichPresence';
import { useVideo } from './providers/VideoContext';
import apiService from './services/apiService';

// Largeur de la vignette d'apercu de la timeline (hauteur deduite en 16/9).
const PREVIEW_WIDTH = 168;

const VideoPlayer = ({ movie, user, allMovies = [], onSelectMovie, onWatchedStatusChange, watchPartyId = null, participants = [] }) => {
    // 🎬 Contexte vidéo pour gérer la visibilité des animations
    const { setIsVideoPlaying, setIsVideoFullscreen } = useVideo();

    const videoRef = useRef(null);
    const wrapperRef = useRef(null);
    const progressBarRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const playerContainerRef = useRef(null);
    const isPlayingRef = useRef(false);
    const isBufferingRef = useRef(false);
    const isFullscreenRef = useRef(false);

    // États progression
    const [savedProgress, setSavedProgress] = useState(null);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [secureVideoUrl, setSecureVideoUrl] = useState('');
    const saveIntervalRef = useRef(null);

    // États contrôles custom
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(() => {
        // 🔊 Récupérer le volume sauvegardé depuis localStorage
        const savedVolume = localStorage.getItem('videoPlayerVolume');
        return savedVolume ? parseFloat(savedVolume) : 1; // Par défaut 100%
    });
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true); // Simple boolean
    const [isBuffering, setIsBuffering] = useState(false);
    const [showCursor, setShowCursor] = useState(true);
    const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
    const [isPiP, setIsPiP] = useState(false);

    // États Chromecast
    const [castAvailable, setCastAvailable] = useState(false);
    const [castConnected, setCastConnected] = useState(false);
    const [castSession, setCastSession] = useState(null);

    // Notifications
    const { notifications, removeNotification } = useNotification();

    // State pour le modal de signalement
    const [showReportModal, setShowReportModal] = useState(false);

    // États pour l'épisode suivant
    const [showNextEpisodeModal, setShowNextEpisodeModal] = useState(false);
    const [nextEpisode, setNextEpisode] = useState(null);
    const nextEpisodeTriggeredRef = useRef(false);

    // État erreur vidéo (MEDIA_ERR_DECODE etc.)
    const [videoError, setVideoError] = useState(null);
    const errorRetryCountRef = useRef(0);
    const errorSavedTimeRef = useRef(0);

    // Référence pour éviter d'ajouter plusieurs fois aux films vus
    const autoWatchedRef = useRef(false);

    // État pour le glissement tactile de la barre de progression
    const [isSeeking, setIsSeeking] = useState(false);

    // Apercu au survol de la timeline : une seconde <video> branchee sur la
    // meme URL, qu'on deplace au fil du survol. Pas de <canvas> : le flux
    // vient d'un autre domaine (backend.popmovie.fr) et le contaminerait des
    // le premier drawImage.
    const previewVideoRef = useRef(null);
    // Les raccourcis clavier lisent ce ref au lieu de capturer des closures :
    // l'ecouteur est pose une seule fois, mais doit voir l'etat courant.
    const shortcutsRef = useRef({});
    const previewSeekRef = useRef({ pending: null, busy: false });
    const [previewMounted, setPreviewMounted] = useState(false);
    const [preview, setPreview] = useState(null); // { time, left } ou null
    const touchSeekingRef = useRef(false);

    // 💜 DISCORD RICH PRESENCE
    /*useDiscordRichPresence({
        movieTitle: movie?.title || '',
        movieId: movie?.id || 0,
        isPlaying,
        currentTime,
        duration,
        moviePoster: movie?.poster || '',
        watchPartyId,
        participants
    });*/

    // Scroll automatique vers le lecteur quand on change d'épisode
    useEffect(() => {
        if (movie?.id && playerContainerRef.current) {
            playerContainerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [movie?.id]);

    // 🔥 Nettoyage du pseudo-fullscreen iOS au démontage
    useEffect(() => {
        return () => {
            if (isPseudoFullscreen && isIOS()) {
                document.body.style.overflow = '';
            }
        };
    }, [isPseudoFullscreen]);

    // 🔥 Gérer l'appui sur Escape pour sortir du pseudo-fullscreen iOS
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isPseudoFullscreen && isIOS()) {
                setIsPseudoFullscreen(false);
                setIsFullscreen(false);
                document.body.style.overflow = '';
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isPseudoFullscreen]);

    // Raccourcis clavier du lecteur
    useEffect(() => {
        const handleShortcuts = (e) => {
            // Ne jamais voler une frappe destinee a une saisie (chat, recherche)
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
            // Laisser passer les raccourcis du navigateur
            if (e.ctrlKey || e.altKey || e.metaKey) return;

            const actions = shortcutsRef.current;
            if (!actions.togglePlay) return;
            let handled = true;

            switch (e.key) {
                case ' ':
                    actions.togglePlay();
                    break;
                case 'ArrowRight':
                    actions.seekBy(10);
                    break;
                case 'ArrowLeft':
                    actions.seekBy(-10);
                    break;
                case 'ArrowUp':
                    actions.nudgeVolume(0.05);
                    break;
                case 'ArrowDown':
                    actions.nudgeVolume(-0.05);
                    break;
                default: {
                    const key = e.key.toLowerCase();
                    if (key === 'm') actions.toggleMute();
                    else if (key === 'f') actions.toggleFullscreen();
                    else if (key === 'p') actions.togglePiP();
                    // 0 a 9 : sauter au dixieme correspondant du film
                    else if (key.length === 1 && key >= '0' && key <= '9') actions.seekToRatio(Number(key) / 10);
                    else handled = false;
                }
            }

            if (handled) {
                e.preventDefault(); // notamment : Espace ne doit pas scroller la page
                handleMouseMove();  // toute action reveille les controles
            }
        };

        window.addEventListener('keydown', handleShortcuts);
        return () => window.removeEventListener('keydown', handleShortcuts);
    }, []); // deps vides OK : tout passe par shortcutsRef et handleMouseMove (useCallback stable)

    // 🔊 Appliquer le volume sauvegardé à la vidéo
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = volume;
        }
    }, [volume, secureVideoUrl]); // Quand le volume change ou qu'une nouvelle vidéo charge

    // Gestion des événements Picture-in-Picture
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleEnterPiP = () => setIsPiP(true);
        const handleLeavePiP = () => setIsPiP(false);

        video.addEventListener('enterpictureinpicture', handleEnterPiP);
        video.addEventListener('leavepictureinpicture', handleLeavePiP);

        return () => {
            video.removeEventListener('enterpictureinpicture', handleEnterPiP);
            video.removeEventListener('leavepictureinpicture', handleLeavePiP);
        };
    }, []);

    // reset quand l’URL change
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !secureVideoUrl) return;

        // Stop propre de l'ancien flux
        try {
            v.pause();
            v.removeAttribute("src"); // important: casse le pipeline
            v.load();                 // reset du decodeur
        } catch (e) {
            // no-op
        }

        // Reset UI player (évite états incohérents au switch)
        setIsBuffering(true);
        setCurrentTime(0);
        setDuration(0);
    }, [secureVideoUrl]);

    // Fonction pour marquer automatiquement comme vu
    const markAsWatchedAutomatically = async () => {
        if (!movie || !user || autoWatchedRef.current) return;

        autoWatchedRef.current = true;

        try {
            const data = await apiService.addWatchedMovie(user.id, movie.id, movie.title);

            if (data.success && onWatchedStatusChange) {
                onWatchedStatusChange();
            }
        } catch (error) {
            console.error('Erreur ajout automatique film vu:', error);
        }
    };

    // 🔥 Détecter la fin de la vidéo et proposer l'épisode suivant
    useEffect(() => {
        if (!movie || !allMovies?.length || !onSelectMovie || movie.type !== 'serie') {
            return;
        }

        // Réinitialiser quand on change d'épisode
        nextEpisodeTriggeredRef.current = false;
        setShowNextEpisodeModal(false);

        // 🎬 NOUVEAU : Réinitialiser aussi l'auto-ajout
        autoWatchedRef.current = false;

        // 🔥 Sortir du fullscreen avant d'afficher le modal
        const exitFullscreenBeforeModal = () => {
            try {
                const video = videoRef.current;

                // iOS
                if (isIOS() && video) {
                    if (video.webkitExitFullscreen) {
                        video.webkitExitFullscreen();
                    } else if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen();
                    }
                }
                // Desktop/Android
                else if (document.fullscreenElement || document.webkitFullscreenElement) {
                    if (document.exitFullscreen) {
                        document.exitFullscreen();
                    } else if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen();
                    } else if (document.mozCancelFullScreen) {
                        document.mozCancelFullScreen();
                    } else if (document.msExitFullscreen) {
                        document.msExitFullscreen();
                    }
                }

                setIsFullscreen(false);
                setIsVideoFullscreen(false); // 🎬 Synchroniser avec le contexte
            } catch (error) {
                console.log('Pas en fullscreen ou erreur');
            }
        };

        const setupListeners = () => {
            const video = videoRef.current;
            if (!video) return;

            const checkAndShowNextEpisode = () => {
                if (nextEpisodeTriggeredRef.current) return;

                const next = findNextEpisode(movie, allMovies);

                if (next) {
                    exitFullscreenBeforeModal(); // 🔥 AJOUTER CETTE LIGNE

                    setNextEpisode(next);
                    setShowNextEpisodeModal(true);
                    nextEpisodeTriggeredRef.current = true;
                }
            };

            const handleTimeUpdate = () => {
                if (nextEpisodeTriggeredRef.current) return;
                if (!video.duration || video.duration === 0) return;

                const currentTime = video.currentTime;
                const duration = video.duration;
                const timeRemaining = duration - currentTime;
                const progress = (currentTime / duration) * 100;

                // 🎬 NOUVEAU : Marquer comme vu à 90% de progression
                if (progress >= 90 && !autoWatchedRef.current) {
                    markAsWatchedAutomatically();
                }

                // 🔥 NOUVEAU TIMING : Seulement dans les 15 dernières secondes
                // Plus précis et ne dérange pas pendant l'épisode
                const shouldTrigger = timeRemaining > 0 && timeRemaining <= 15;

                if (shouldTrigger) {
                    checkAndShowNextEpisode();
                }
            };

            const handleEnded = () => {
                // 🎬 NOUVEAU : Marquer comme vu si pas encore fait
                if (!autoWatchedRef.current) {
                    markAsWatchedAutomatically();
                }

                if (!nextEpisodeTriggeredRef.current) {
                    checkAndShowNextEpisode();
                }
            };

            video.addEventListener('timeupdate', handleTimeUpdate);
            video.addEventListener('ended', handleEnded);

            return () => {
                video.removeEventListener('timeupdate', handleTimeUpdate);
                video.removeEventListener('ended', handleEnded);
            };
        };

        // Attendre que la vidéo soit chargée
        const video = videoRef.current;
        if (video) {
            return setupListeners();
        } else {
            const checkInterval = setInterval(() => {
                if (videoRef.current) {
                    clearInterval(checkInterval);
                    setupListeners();
                }
            }, 100);

            return () => {
                clearInterval(checkInterval);
            };
        }
    }, [movie?.id, allMovies, onSelectMovie]);

    // 🔥 NOUVEAU : Gérer le passage à l'épisode suivant
    const handlePlayNextEpisode = () => {
        if (nextEpisode && onSelectMovie) {
            setShowNextEpisodeModal(false);
            // 🔥 Sauvegarder l'intention d'autoplay dans sessionStorage
            sessionStorage.setItem('shouldAutoPlay', 'true');
            onSelectMovie(nextEpisode);
        }
    };

    const handleCancelNextEpisode = () => {
        setShowNextEpisodeModal(false);
        nextEpisodeTriggeredRef.current = true; // Empêcher de réafficher
    };

    // Générer une URL sécurisée pour la vidéo
    useEffect(() => {
        if (!movie?.id || !user?.id) return;

        const token = localStorage.getItem('cinestream_token');
        if (!token) {
            console.error('Token non trouvé');
            return;
        }

        const secureUrl = `https://backend.popmovie.fr/api/stream/${movie.id}?token=${token}`;
        setSecureVideoUrl(secureUrl);
        // Réinitialiser l'état d'erreur pour le nouveau film
        setVideoError(null);
        errorRetryCountRef.current = 0;
        errorSavedTimeRef.current = 0;
        // La vignette d'apercu recharge : sa file de seeks ne vaut plus rien
        previewSeekRef.current = { pending: null, busy: false };
        setPreview(null);
    }, [movie?.id, user?.id]);


    // Détecter fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isNowFullscreen = !!(document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement);
            setIsFullscreen(isNowFullscreen);
            setIsVideoFullscreen(isNowFullscreen); // 🎬 Synchroniser avec le contexte
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Sync états avec vidéo
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const updateStates = () => {
            setIsPlaying(!video.paused);
            //setIsVideoPlaying(!video.paused); // 🎬 Synchroniser avec le contexte
            setCurrentTime(video.currentTime);
            setDuration(video.duration || 0);
            const newVolume = video.volume;
            setVolume(newVolume);
            // 🔊 Sauvegarder le volume en localStorage
            localStorage.setItem('videoPlayerVolume', newVolume.toString());
            setIsMuted(video.muted);
        };

        const handleLoadedMetadata = () => {
            setDuration(video.duration);
        };

        const handleTimeUpdate = () => {
            setCurrentTime(video.currentTime);
        };

        const handlePlay = () => {
            setIsPlaying(true);
            setIsBuffering(false);
            setIsVideoPlaying(true);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setIsVideoPlaying(false);
        };

        const handlePause = () => {
            setIsPlaying(false);
            setIsVideoPlaying(false);
        };

        const handleVolumeChange = () => {
            const newVolume = video.volume;
            setVolume(newVolume);
            // 🔊 Sauvegarder le volume en localStorage
            localStorage.setItem('videoPlayerVolume', newVolume.toString());
            setIsMuted(video.muted);
        };

        const handleWaiting = () => {
            setIsBuffering(true);
        };

        const handleCanPlay = () => {

            // 🔥 Autoplay de l'épisode suivant quand la vidéo est prête
            if (sessionStorage.getItem('shouldAutoPlay') === 'true') {
                video.play()
                    .then(() => {
                        sessionStorage.removeItem('shouldAutoPlay');
                    })
                    .catch(err => {
                        console.error('⚠️ Erreur autoplay:', err);
                        // Réessayer après un court délai
                        setTimeout(() => {
                            if (sessionStorage.getItem('shouldAutoPlay') === 'true') {
                                video.play().catch(e => console.error('⚠️ Échec autoplay retry:', e));
                                sessionStorage.removeItem('shouldAutoPlay');
                            }
                        }, 300);
                    });
            }
            setIsBuffering(false);
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('play', handlePlay);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('pause', handlePause);
        video.addEventListener('volumechange', handleVolumeChange);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('canplay', handleCanPlay);

        updateStates();

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('volumechange', handleVolumeChange);
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('canplay', handleCanPlay);
        };
    }, [secureVideoUrl]);

    // PING UNIQUE : Seulement si on regarde vraiment quelque chose
    useEffect(() => {
        if (!user?.id || !user?.username || !movie?.id || !isPlaying || !duration) {
            return; // Pas de vidéo active → App.js s'en occupe
        }

        const sendSessionPing = () => {
            const payload = {
                user_id: user.id,
                username: user.username,
                movie_id: movie.id,
                movie_title: movie.title,
                type: movie.type,
                current_time: currentTime,
                duration: duration,
            };

            if (movie.type === 'serie') {
                payload.season = movie.season;
                payload.episode = movie.episode;
            }

            apiService.sendSessionPing(payload).catch(err =>
                console.error('Erreur ping vidéo:', err)
            );
        };

        sendSessionPing();
        const interval = setInterval(sendSessionPing, 30000);

        return () => clearInterval(interval);
    }, [user?.id, user?.username, movie?.id, movie?.title, movie?.type, movie?.season, movie?.episode, isPlaying, currentTime, duration]);

    // Synchroniser les refs avec les états pour éviter les stale closures dans les timeouts
    useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
    useEffect(() => { isBufferingRef.current = isBuffering; }, [isBuffering]);
    useEffect(() => { isFullscreenRef.current = isFullscreen; }, [isFullscreen]);

    // Référence stable (deps vides) — ne jamais recréer pour éviter les re-registrations du listener
    const handleMouseMove = React.useCallback(() => {
        setShowControls(true);
        setShowCursor(true);

        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }

        const delay = isFullscreenRef.current ? 5000 : 3000;

        inactivityTimeoutRef.current = setTimeout(() => {
            // Utilise les refs pour lire les valeurs COURANTES, pas celles du moment de création
            if (isPlayingRef.current && !isBufferingRef.current) {
                setShowControls(false);
                setShowCursor(false);
            }
        }, delay);
    }, []); // deps vides : la fonction ne change jamais de référence

    useEffect(() => {
        const onAnyMove = () => handleMouseMove();
        const opts = { capture: true, passive: true };

        // mousemove = plus fiable pour les souris physiques (multi-écrans, fenêtre non-focus)
        // pointermove = couvre les écrans tactiles et stylets
        document.addEventListener("mousemove", onAnyMove, opts);
        document.addEventListener("pointermove", onAnyMove, opts);

        return () => {
            document.removeEventListener("mousemove", onAnyMove, opts);
            document.removeEventListener("pointermove", onAnyMove, opts);
        };
    }, [handleMouseMove]);

    // plantage video
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;

        // On ne loggue stalled que si on a vraiment démarré la lecture une fois
        // (sinon tu auras du bruit quand tu ouvres le player sans autoplay)
        //const shouldLogStalled = () => {
        //    // on considère qu'on a "essayé de lire" si currentTime > 0 ou readyState >= 2
        //    // (tu peux aussi mettre un ref hasStartedPlaybackRef si tu préfères)
        //    return v.currentTime > 0.1 || v.readyState >= 2;
        //};

        const log = (type, extraMessage = "") => {
            const payload = {
                // ✅ ton PHP exige message
                message: `Player event: ${type}${extraMessage ? ` | ${extraMessage}` : ""}`,

                // champs déjà supportés par ton PHP
                timestamp: new Date().toISOString(),
                url: window.location.href,

                // ---- extras utiles (on va patcher ton PHP pour les sauvegarder) ----
                type,
                movie_id: movie?.id ?? null,
                video_url: secureVideoUrl ?? null,

                // Etat HTMLMediaElement
                errorCode: v.error?.code ?? null,
                readyState: v.readyState,
                networkState: v.networkState,
                currentTime: Number.isFinite(v.currentTime) ? v.currentTime : null,
                duration: Number.isFinite(v.duration) ? v.duration : null,
                paused: v.paused,

                // info de debug pratique
                src: v.currentSrc || null,

                // Others
                visibility: document.visibilityState,
                online: navigator.onLine,
            };

            fetch("https://backend.popmovie.fr/api/errors", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            }).catch(() => {});
        };

        const onErr = () => {
            const code = v.error?.code ?? 0;
            log("video.error", `code=${code}`);

            // MEDIA_ERR_DECODE (code 3) ou MEDIA_ERR_NETWORK (code 2) : tentative de récupération
            if (code === 3 || code === 2) {
                const savedTime = v.currentTime || 0;
                errorSavedTimeRef.current = savedTime;

                if (errorRetryCountRef.current < 3) {
                    errorRetryCountRef.current += 1;
                    setIsBuffering(true);
                    setVideoError(null);

                    setTimeout(() => {
                        const vid = videoRef.current;
                        if (!vid) return;
                        vid.load();
                        const onCanPlayOnce = () => {
                            vid.removeEventListener('canplay', onCanPlayOnce);
                            vid.currentTime = errorSavedTimeRef.current;
                            vid.play().then(() => {
                                setIsBuffering(false);
                            }).catch(() => {
                                setIsBuffering(false);
                                setVideoError(code);
                            });
                        };
                        vid.addEventListener('canplay', onCanPlayOnce);
                    }, 1000);
                } else {
                    // Trop d'essais, afficher l'erreur
                    setIsBuffering(false);
                    setVideoError(code);
                }
            } else {
                setVideoError(code);
            }
        };

        //const onStalled = () => {
        //    if (!shouldLogStalled()) return;
        //    log("video.stalled");
        //};

        v.addEventListener("error", onErr);
        //v.addEventListener("stalled", onStalled);

        return () => {
            v.removeEventListener("error", onErr);
            //v.removeEventListener("stalled", onStalled);
        };
    }, [secureVideoUrl, movie?.id]);


    // Afficher les contrôles quand la vidéo est en pause ou buffering
    useEffect(() => {
        if (!isPlaying || isBuffering) {
            setShowControls(true);
            setShowCursor(true);
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }
        }
    }, [isPlaying, isBuffering]);

    /// Gérer touchstart en plein écran pour mobile
    useEffect(() => {
        if (!isFullscreen) return;

        // Sur mobile, un tap affiche les contrôles
        const handleTouch = () => {
            handleMouseMove();
        };

        document.addEventListener('touchstart', handleTouch);

        return () => {
            document.removeEventListener('touchstart', handleTouch);
        };
    }, [isFullscreen, handleMouseMove]);

    // CHROMECAST : Initialisation robuste + logs
    useEffect(() => {
        const initializeCast = () => {
            // Vérifier que le SDK est vraiment là
            if (!window.cast || !window.cast.framework || !window.chrome?.cast) {
                return;
            }

            const context = window.cast.framework.CastContext.getInstance();

            // Obligatoire pour que le framework fonctionne bien
            context.setOptions({
                receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
                autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.TAB_AND_ORIGIN_SCOPED,
            });

            // État initial
            const initialState = context.getCastState();
            setCastAvailable(
                initialState !== window.cast.framework.CastState.NO_DEVICES_AVAILABLE
            );

            const handleCastStateChanged = (event) => {
                const hasDevices =
                    event.castState &&
                    event.castState !== window.cast.framework.CastState.NO_DEVICES_AVAILABLE;

                setCastAvailable(hasDevices);
            };

            const handleSessionStateChanged = (event) => {

                const session = context.getCurrentSession();
                setCastSession(session);

                const isSessionActive =
                    event.sessionState === window.cast.framework.SessionState.SESSION_STARTED ||
                    event.sessionState === window.cast.framework.SessionState.SESSION_RESUMED;

                setCastConnected(isSessionActive);
            };

            context.addEventListener(
                window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
                handleCastStateChanged
            );
            context.addEventListener(
                window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
                handleSessionStateChanged
            );
        };

        // Gestion de __onGCastApiAvailable (appelé par le script Google)
        const attachCallback = () => {
            if (typeof window.__onGCastApiAvailable === 'function') {
                const previous = window.__onGCastApiAvailable;
                window.__onGCastApiAvailable = (isAvailable) => {
                    previous(isAvailable);
                    if (isAvailable) initializeCast();
                };
            } else {
                window.__onGCastApiAvailable = (isAvailable) => {
                    if (isAvailable) initializeCast();
                };
            }
        };

        // Si le SDK est déjà prêt
        if (window.cast && window.cast.framework && window.chrome?.cast) {
            initializeCast();
        } else {
            attachCallback();
        }
    }, []);

    // CHROMECAST : Fonction pour caster la vidéo
    const castVideo = async () => {
        if (!castSession || !secureVideoUrl || !movie) return;

        try {
            let currentTime = 0;
            // 🔥 CHANGEMENT : Ne pas mettre en pause la vidéo locale immédiatement
            // On récupère juste le currentTime
            if (videoRef.current) {
                currentTime = videoRef.current.currentTime;
            }

            const fullVideoUrl = `${window.location.origin}${secureVideoUrl}`;

            const mediaInfo = new window.chrome.cast.media.MediaInfo(fullVideoUrl, 'video/mp4');
            mediaInfo.metadata = new window.chrome.cast.media.MovieMediaMetadata();
            mediaInfo.metadata.title = movie.title;
            mediaInfo.metadata.subtitle = movie.year;

            if (movie.poster) {
                mediaInfo.metadata.images = [
                    new window.chrome.cast.Image(`${window.location.origin}${movie.poster}`)
                ];
            }

            const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
            request.currentTime = currentTime || 0;
            request.autoplay = true;

            const media = castSession.getMediaSession();
            if (media) {
                await media.stop();
            }

            await castSession.loadMedia(request);

            //  Mettre en pause la vidéo locale APRÈS avoir casté
            if (videoRef.current) {
                videoRef.current.pause();
            }
        } catch (error) {
            console.error('Erreur lors du cast:', error);
        }
    };

    // CHROMECAST: Bouton Cast
    const handleCastClick = () => {
        if (!window.cast || !window.cast.framework) {
            return;
        }

        const context = window.cast.framework.CastContext.getInstance();

        if (castConnected) {
            castVideo();
        } else {
            context.requestSession()
                .then(() => {
                    setTimeout(() => castVideo(), 700);
                })
                .catch((error) => {
                    if (error !== 'cancel') {
                        console.error('Erreur Chromecast:', error);
                    }
                });
        }
    };

    // Synchroniser l'état play/pause avec le Chromecast
    useEffect(() => {
        if (!castSession) return;

        const updateInterval = setInterval(() => {
            const media = castSession.getMediaSession();
            if (media) {
                // Mettre à jour currentTime et isPlaying depuis le Chromecast
                const estimatedTime = media.getEstimatedTime();
                if (estimatedTime >= 0) {
                    setCurrentTime(estimatedTime);
                }
                const isPlayingCast = media.playerState === window.chrome.cast.media.PlayerState.PLAYING;
                setIsPlaying(isPlayingCast);
                setIsVideoPlaying(isPlayingCast); // 🎬 Synchroniser avec le contexte
            }
        }, 500); // Toutes les 500ms

        return () => clearInterval(updateInterval);
    }, [castSession]);

    // Charger la position sauvegardée - Utiliser movie.id directement
    useEffect(() => {
        if (!movie?.id || !user?.id) return;

        // Réinitialiser les états quand on change de film/épisode
        setSavedProgress(null);
        setShowResumeModal(false);

        // 🔥 UTILISER movie.id TEL QUEL (déjà au bon format dans movies-data.json)
        apiService.getProgress(user.id, movie.id)
            .then(data => {
                if (data.success && data.progress) {
                    const { position, duration, percentage } = data.progress;

                    if (percentage < 95 && position > 30) {
                        setSavedProgress({
                            position,
                            duration,
                            percentage
                        });
                        // 🔥 Si l'épisode suivant est lancé automatiquement, ne pas afficher le modal
                        if (sessionStorage.getItem('shouldAutoPlay') === 'true') {
                            // Ne pas afficher le modal, l'autoplay se chargera de lancer la vidéo
                            setShowResumeModal(false);
                        } else {
                            setShowResumeModal(true);
                        }
                    }
                }
            })
            .catch(err => console.error('Erreur chargement progression:', err));

        return () => {
            if (saveIntervalRef.current) {
                clearInterval(saveIntervalRef.current);
            }
        };
    }, [movie?.id, user?.id]);

    // Fonction pour formater le temps
    const formatTime = (seconds) => {
        if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);

        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // Sauvegarder la progression - Utiliser movie.id directement
    const saveProgress = async (currentTime, duration) => {
        if (!movie?.id || !user?.id || !duration || isNaN(currentTime)) return;

        const percentage = (currentTime / duration) * 100;

        if (currentTime < 30 || percentage > 95) {
            if (percentage > 95) {
                await apiService.removeProgress(user.id, movie.id);
            }
            return;
        }

        try {
            await apiService.saveProgress(user.id, movie.id, currentTime, duration, percentage, movie.title);
        } catch (error) {
            console.error('Erreur sauvegarde progression:', error);
        }
    };

    // Reprendre la lecture
    const handleResume = () => {
        if (videoRef.current && savedProgress) {
            videoRef.current.currentTime = savedProgress.position;
            setShowResumeModal(false);
            videoRef.current.play();
        }
    };

    // Recommencer depuis le début
    const handleRestart = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            setShowResumeModal(false);
            videoRef.current.play();
        }
    };

    // Fermer la modal sans faire de choix
    const handleCloseResumeModal = () => {
        setShowResumeModal(false);
    };

    // Démarrer l'intervalle de sauvegarde quand la vidéo démarre
    const handlePlay = () => {
        setIsVideoPlaying(true); // ✅ coupe les flocons dès que ça joue

        if (saveIntervalRef.current) {
            clearInterval(saveIntervalRef.current);
        }

        saveIntervalRef.current = setInterval(() => {
            if (videoRef.current && !videoRef.current.paused && !videoRef.current.ended) {
                saveProgress(videoRef.current.currentTime, videoRef.current.duration);
            }
        }, 10000);
    };

    // Arrêter l'intervalle quand la vidéo est en pause
    const handlePause = () => {
        setIsVideoPlaying(false); // ✅ remet les flocons en pause

        if (saveIntervalRef.current) {
            clearInterval(saveIntervalRef.current);
        }

        if (videoRef.current) {
            saveProgress(videoRef.current.currentTime, videoRef.current.duration);
        }
    };

    // Quand la vidéo se termine - Utiliser movie.id directement
    const handleEnded = () => {
        setIsVideoPlaying(false); // ✅ déjà présent, on garde

        if (saveIntervalRef.current) {
            clearInterval(saveIntervalRef.current);
        }

        apiService.removeProgress(user.id, movie.id).catch(() => {});

        localStorage.removeItem(`video-pos:${movie.id}`);
    };


    // 🎬 Cleanup contexte vidéo au démontage
    useEffect(() => {
        return () => {
            setIsVideoPlaying(false);
            setIsVideoFullscreen(false);
        };
    }, [setIsVideoPlaying, setIsVideoFullscreen]);

    // Cleanup et sauvegarde finale - Utiliser movie.id directement
    useEffect(() => {
        const saveOnUnload = () => {
            if (videoRef.current && videoRef.current.currentTime > 0) {
                if (navigator.sendBeacon) {
                    apiService.saveProgressBeacon(
                        user.id,
                        movie.id,
                        videoRef.current.currentTime,
                        videoRef.current.duration,
                        (videoRef.current.currentTime / videoRef.current.duration) * 100,
                        movie.title
                    );
                } else {
                    localStorage.setItem(`video-pos:${movie.id}`, JSON.stringify({
                        position: videoRef.current.currentTime,
                        duration: videoRef.current.duration,
                        percentage: (videoRef.current.currentTime / videoRef.current.duration) * 100,
                        timestamp: Date.now()
                    }));
                }
            }
        };

        window.addEventListener('beforeunload', saveOnUnload);

        return () => {
            window.removeEventListener('beforeunload', saveOnUnload);
            if (saveIntervalRef.current) {
                clearInterval(saveIntervalRef.current);
            }
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }
            saveOnUnload();
        };
    }, [user?.id, movie?.id]);

    // Contrôles custom
    const togglePlay = () => {
        // 🔥 Si casté, contrôler le Chromecast
        if (castConnected && castSession) {
            const media = castSession.getMediaSession();
            if (media) {
                if (media.playerState === window.chrome.cast.media.PlayerState.PLAYING) {
                    media.pause();
                } else {
                    media.play();
                }
                return; // Ne pas toucher à la vidéo locale
            }
        }

        // Sinon, contrôler la vidéo locale normalement
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    };

    const toggleMute = () => {
        // Si casté, mute/unmute le Chromecast
        if (castConnected && castSession) {
            const media = castSession.getMediaSession();
            if (media) {
                const currentVolume = media.volume ? media.volume.level : 1;
                const isMuted = media.volume ? media.volume.muted : false;

                const request = new window.chrome.cast.media.VolumeRequest();
                request.volume = new window.chrome.cast.Volume(currentVolume, !isMuted);
                media.setVolume(request);
            }
        }

        // Toujours mettre à jour la vidéo locale
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
        }
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);

        // Si casté, changer le volume du Chromecast
        if (castConnected && castSession) {
            const media = castSession.getMediaSession();
            if (media) {
                const isMuted = media.volume ? media.volume.muted : false;
                const request = new window.chrome.cast.media.VolumeRequest();
                request.volume = new window.chrome.cast.Volume(newVolume, isMuted);
                media.setVolume(request);
            }
        }

        // Toujours mettre à jour la vidéo locale (pour quand on déconnecte)
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
        }
    };

    // Un seek chasse l'autre : on ne retient que la derniere position visee
    // et on attend « seeked » avant de repartir. Sans ca, un balayage de la
    // barre lance des dizaines de requetes Range dont on jette le resultat.
    const drainPreviewSeek = () => {
        const v = previewVideoRef.current;
        const queue = previewSeekRef.current;
        if (!v || queue.pending === null || v.readyState < 1) {
            queue.busy = false;
            return;
        }
        const target = queue.pending;
        queue.pending = null;
        queue.busy = true;
        try {
            v.currentTime = target;
        } catch (err) {
            queue.busy = false;
        }
    };

    const handleProgressHover = (e) => {
        const bar = progressBarRef.current;
        if (!bar || duration <= 0) return;
        // Le tactile a deja son propre glissement, et une vignette sans
        // curseur n'a pas de sens : on ne depense la bande passante que pour
        // un vrai pointeur.
        if (!window.matchMedia('(pointer: fine)').matches) return;

        const rect = bar.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const half = PREVIEW_WIDTH / 2;

        setPreviewMounted(true);
        setPreview({
            time: ratio * duration,
            // Bornage : la vignette suit le curseur sans deborder de la barre
            left: Math.min(Math.max(ratio * rect.width, half), Math.max(rect.width - half, half)),
        });

        previewSeekRef.current.pending = ratio * duration;
        if (!previewSeekRef.current.busy) drainPreviewSeek();
    };

    const handleProgressClick = (e) => {
        const bar = progressBarRef.current;
        if (!bar || duration <= 0) return;

        const rect = bar.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        const newTime = pos * duration;

        // Si casté, seek sur le Chromecast
        if (castConnected && castSession) {
            const media = castSession.getMediaSession();
            if (media) {
                const request = new window.chrome.cast.media.SeekRequest();
                request.currentTime = newTime;
                media.seek(request);
                return;
            }
        }

        // Sinon, seek sur la vidéo locale
        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
        }
    };

    // Gestion tactile pour la barre de progression
    const updateProgressFromTouch = (clientX) => {
        const bar = progressBarRef.current;
        if (!bar || duration <= 0) return;

        const rect = bar.getBoundingClientRect();
        const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const newTime = pos * duration;

        // Si casté, seek sur le Chromecast
        if (castConnected && castSession) {
            const media = castSession.getMediaSession();
            if (media) {
                const request = new window.chrome.cast.media.SeekRequest();
                request.currentTime = newTime;
                media.seek(request);
                return;
            }
        }

        // Sinon, seek sur la vidéo locale
        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
        }
    };

    const handleTouchStart = (e) => {
        e.stopPropagation();
        touchSeekingRef.current = true;
        setIsSeeking(true);
        const touch = e.touches[0];
        updateProgressFromTouch(touch.clientX);
    };

    const handleTouchMove = (e) => {
        if (!touchSeekingRef.current) return;
        e.preventDefault(); // Empêche le scroll pendant le glissement
        e.stopPropagation();
        const touch = e.touches[0];
        updateProgressFromTouch(touch.clientX);
    };

    const handleTouchEnd = (e) => {
        e.stopPropagation();
        touchSeekingRef.current = false;
        setIsSeeking(false);
    };

    // Détection iOS/Safari pour le plein écran
    const isIOS = () => {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    };

    const toggleFullscreen = async () => {
        try {
            const video = videoRef.current;
            const wrapper = wrapperRef.current;

            // iOS/Safari : Utilise le plein écran natif de la vidéo
            if (isIOS() && video) {
                if (!isFullscreen) {
                    if (video.webkitEnterFullscreen) {
                        video.webkitEnterFullscreen();
                    } else if (video.webkitRequestFullscreen) {
                        await video.webkitRequestFullscreen();
                    }
                } else {
                    if (video.webkitExitFullscreen) {
                        video.webkitExitFullscreen();
                    } else if (document.webkitExitFullscreen) {
                        await document.webkitExitFullscreen();
                    }
                }
                return;
            }

            // Desktop/Android : Utilise le plein écran sur le wrapper
            if (!isFullscreen) {
                if (wrapper.requestFullscreen) {
                    await wrapper.requestFullscreen();
                } else if (wrapper.webkitRequestFullscreen) {
                    await wrapper.webkitRequestFullscreen();
                } else if (wrapper.mozRequestFullScreen) {
                    await wrapper.mozRequestFullScreen();
                } else if (wrapper.msRequestFullscreen) {
                    await wrapper.msRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    await document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }
            }
        } catch (error) {
            console.error('Erreur fullscreen:', error);
        }
    };

    // Picture-in-Picture
    const togglePiP = async () => {
        try {
            const video = videoRef.current;
            if (!video) return;

            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
            } else {
                await video.requestPictureInPicture();
            }
        } catch (error) {
            console.error('Erreur PiP:', error);
        }
    };

    // Deplace la lecture, sur le Chromecast s'il y en a un, sinon en local.
    const seekToTime = (newTime) => {
        if (!Number.isFinite(newTime)) return;

        if (castConnected && castSession) {
            const media = castSession.getMediaSession();
            if (media) {
                const request = new window.chrome.cast.media.SeekRequest();
                request.currentTime = newTime;
                media.seek(request);
                return;
            }
        }

        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
        }
    };

    // Reassigne a chaque rendu : les raccourcis voient toujours l'etat frais.
    shortcutsRef.current = {
        togglePlay,
        toggleMute,
        toggleFullscreen,
        togglePiP,
        seekBy: (delta) => {
            const v = videoRef.current;
            if (!v || !Number.isFinite(v.duration)) return;
            seekToTime(Math.max(0, Math.min(v.duration, v.currentTime + delta)));
        },
        seekToRatio: (ratio) => {
            const v = videoRef.current;
            if (!v || !Number.isFinite(v.duration)) return;
            seekToTime(ratio * v.duration);
        },
        nudgeVolume: (delta) => {
            const v = videoRef.current;
            if (!v) return;
            const next = Math.max(0, Math.min(1, v.volume + delta));
            v.volume = next;
            setVolume(next);
            if (next > 0 && v.muted) v.muted = false;
        },
    };

    // Clic sur la vidéo pour play/pause
    const handleVideoClick = (e) => {
        if (e.target.closest('.video-controls')) return;
        togglePlay();
    };

    if (!secureVideoUrl) {
        return (
            <div className="vp-boot">
                <div className="vp-boot-inner">
                    <div className="vp-spinner" />
                    <p className="vp-boot-text">Préparation de la vidéo...</p>
                </div>
            </div>
        );
    }

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <>
            {/* Modal reprise */}
            {showResumeModal && savedProgress && (
                <div className="vp-resume">
                    <div className="vp-resume-card">
                        <button onClick={handleCloseResumeModal} className="vp-resume-close">
                            <X />
                        </button>

                        <div className="vp-resume-badge">
                            <div><Clock /></div>
                        </div>

                        <h3 className="vp-resume-title">Reprendre la lecture ?</h3>

                        <div className="vp-resume-box">
                            <p className="vp-resume-line">
                                Progression : <span className="vp-resume-pct">{savedProgress.percentage.toFixed(0)}%</span>
                            </p>

                            <div className="vp-resume-track">
                                <div className="vp-resume-fill" style={{ width: `${savedProgress.percentage}%` }} />
                            </div>

                            <div className="vp-resume-times">
                                <span>{formatTime(savedProgress.position)}</span>
                                <span>{formatTime(savedProgress.duration)}</span>
                            </div>
                        </div>

                        <div className="vp-resume-actions">
                            <button onClick={handleResume} className="btn btn-gold btn-block">
                                <Play />
                                Reprendre la lecture
                            </button>

                            <button onClick={handleRestart} className="btn btn-ghost btn-block">
                                <RotateCcw />
                                Recommencer depuis le début
                            </button>
                        </div>

                        <p className="vp-resume-note">
                            Progression synchronisée sur tous vos appareils
                        </p>
                    </div>
                </div>
            )}

            {/* Référence pour le scroll automatique */}
            <div ref={playerContainerRef}>
                {/* WRAPPER FULLSCREEN */}
                <div
                    ref={wrapperRef}
                    className={`relative bg-black group ${isFullscreen ? 'w-screen h-screen' : 'aspect-video'} ${showCursor ? 'cursor-default' : 'cursor-none'}`}
                    onClick={handleVideoClick}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseMove}
                    onPointerMove={handleMouseMove}
                    onPointerEnter={handleMouseMove}
                >
                    {/* Vidéo avec contrôles natifs CACHÉS */}
                    <video
                        key={movie?.id || secureVideoUrl}
                        ref={videoRef}
                        controlsList="nodownload noremoteplayback"
                        playsInline
                        preload="metadata"
                        className="w-full h-full vp-video [&::-webkit-media-controls]:opacity-0 [&::-webkit-media-controls-enclosure]:opacity-0"
                        poster={movie.backdrop || movie.poster}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        onEnded={handleEnded}
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        <source src={secureVideoUrl} type="video/mp4" />
                        Votre navigateur ne supporte pas la lecture vidéo.
                    </video>

                    {/* NOTIFICATIONS EN FULLSCREEN */}
                    {isFullscreen && notifications.length > 0 && (
                        <div className="absolute top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
                            <div className="flex flex-col gap-3 pointer-events-auto">
                                {notifications.map((notification) => (
                                    <Notification
                                        key={notification.id}
                                        notification={notification}
                                        onClose={removeNotification}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 🎬 BANDEAU EN HAUT - TITRE (style YouTube) */}
                    <div
                        className={`absolute top-0 left-0 right-0 z-30 transition-all duration-300 pointer-events-none ${
                            showControls ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
                        }`}
                    >
                        <div className="bg-gradient-to-b from-black/80 via-black/40 to-transparent px-4 sm:px-6 py-4 sm:py-6">
                            <div className="max-w-4xl">
                                {/* Titre principal */}
                                <h1 className="text-white text-lg sm:text-xl md:text-2xl font-bold drop-shadow-lg">
                                    {movie.title}
                                </h1>

                                {/* Sous-titre (saison + épisode pour les séries) */}
                                {movie.type === 'serie' && movie.season && movie.episode && (
                                    <p className="text-gray-300 text-sm sm:text-base mt-1 drop-shadow-lg">
                                        Saison {movie.season} · Épisode {movie.episode}
                                        {movie.episodeTitle && ` · ${movie.episodeTitle}`}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Indicateur buffering */}
                    {isBuffering && (
                        <div className="vp-veil">
                            <div className="vp-buffer" />
                        </div>
                    )}

                    {/* Overlay erreur vidéo */}
                    {videoError && (
                        <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/80 backdrop-blur-sm">
                            <div className="text-center px-6 py-8 max-w-sm">
                                <AlertTriangle className="w-14 h-14 text-red-400 mx-auto mb-4" />
                                <p className="text-white text-lg font-semibold mb-2">Erreur de lecture</p>
                                <p className="text-gray-400 text-sm mb-6">
                                    {videoError === 3
                                        ? "Le fichier vidéo n'a pas pu être décodé. Essaie de recharger."
                                        : videoError === 2
                                        ? "Problème réseau lors du chargement de la vidéo."
                                        : `Erreur inattendue (code ${videoError}).`}
                                </p>
                                <button
                                    onClick={() => {
                                        errorRetryCountRef.current = 0;
                                        setVideoError(null);
                                        const vid = videoRef.current;
                                        if (!vid) return;
                                        const t = errorSavedTimeRef.current;
                                        setIsBuffering(true);
                                        vid.load();
                                        const onReady = () => {
                                            vid.removeEventListener('canplay', onReady);
                                            vid.currentTime = t;
                                            vid.play().then(() => setIsBuffering(false)).catch(() => setIsBuffering(false));
                                        };
                                        vid.addEventListener('canplay', onReady);
                                    }}
                                    className="btn btn-gold"
                                >
                                    Réessayer
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Bouton Play central */}
                    {!isPlaying && !isBuffering && !videoError && (
                        <div className="vp-veil">
                            <button
                                onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                                className="play-btn vp-big-play"
                            >
                                <Play fill="currentColor" />
                            </button>
                        </div>
                    )}

                    {/* Indicateur Chromecast */}
                    {castConnected && (
                        <div className="vp-casting">
                            <Cast className="animate-pulse" />
                            <span className="hidden sm:inline">Casting en cours...</span>
                            <span className="sm:hidden">Casting...</span>
                        </div>
                    )}

                    {/* Contrôles personnalisés */}
                    <div
                        className={`video-controls vp-controls${showControls ? '' : ' is-hidden'}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="vp-scrim" />

                        {/* Timeline */}
                        <div className="vp-bar-wrap">
                            <div
                                ref={progressBarRef}
                                className={`vp-bar${isSeeking ? ' is-seeking' : ''}`}
                                onClick={handleProgressClick}
                                onMouseMove={handleProgressHover}
                                onMouseLeave={() => setPreview(null)}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                {/* Vignette d'apercu : montee au premier survol
                                    puis gardee en place. La demonter fermerait
                                    la connexion, et le survol suivant repartirait
                                    d'un chargement de metadonnees. */}
                                {previewMounted && (
                                    <div
                                        className={`vp-preview${preview ? ' is-on' : ''}`}
                                        style={{ '--vp-preview-x': `${preview ? preview.left : 0}px` }}
                                    >
                                        <div className="vp-preview-frame">
                                            <video
                                                ref={previewVideoRef}
                                                src={secureVideoUrl}
                                                muted
                                                playsInline
                                                preload="metadata"
                                                tabIndex={-1}
                                                aria-hidden="true"
                                                onLoadedMetadata={drainPreviewSeek}
                                                onSeeked={drainPreviewSeek}
                                                onError={() => { previewSeekRef.current.busy = false; }}
                                            />
                                        </div>
                                        <div className="vp-preview-time">
                                            {formatTime(preview ? preview.time : 0)}
                                        </div>
                                    </div>
                                )}
                                <div className="vp-bar-fill" style={{ width: `${progress}%` }}>
                                    <div className="vp-bar-knob" />
                                </div>
                            </div>
                        </div>

                        {/* Rangée de contrôles */}
                        <div className="vp-row">
                            <div className="vp-group">
                                <button onClick={togglePlay} className="vp-btn vp-btn--lead">
                                    {isPlaying
                                        ? <Pause fill="currentColor" />
                                        : <Play fill="currentColor" />
                                    }
                                </button>

                                <button onClick={toggleMute} className="vp-btn vp-btn--sound">
                                    {isMuted || volume === 0 ? <VolumeX /> : <Volume2 />}
                                </button>

                                <input
                                    type="range" min="0" max="1" step="0.1"
                                    value={isMuted ? 0 : volume}
                                    onChange={handleVolumeChange}
                                    className="hidden sm:block vp-volume"
                                    style={{ '--vp-vol': isMuted ? 0 : volume }}
                                />

                                <div className="hidden sm:block vp-time">
                                    <span>{formatTime(currentTime)}</span>
                                    <span className="vp-time-sep">/</span>
                                    <span className="vp-time-total">{formatTime(duration)}</span>
                                </div>
                                <div className="sm:hidden vp-time--compact">
                                    {formatTime(currentTime)}
                                </div>
                            </div>

                            <div className="vp-group vp-group--right">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowReportModal(true); }}
                                    className="vp-btn vp-btn--report"
                                    title="Signaler un problème"
                                >
                                    <AlertTriangle />
                                </button>

                                <button
                                    onClick={(e) => { e.stopPropagation(); togglePiP(); }}
                                    className={`hidden sm:flex vp-btn${isPiP ? ' is-on' : ''}`}
                                    title={isPiP ? 'Quitter PiP' : 'Mode Fenêtre'}
                                >
                                    <PictureInPicture />
                                </button>

                                <button
                                    onClick={(e) => { e.stopPropagation(); handleCastClick(); }}
                                    className={`vp-btn vp-btn--cast${castConnected ? ' is-on' : ''}`}
                                    title={castConnected ? 'Connecté à Chromecast' : castAvailable ? 'Caster sur Chromecast' : 'Chromecast non disponible'}
                                    disabled={!castAvailable && !castConnected}
                                >
                                    <Cast />
                                </button>

                                <button
                                    onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                                    className="vp-btn"
                                    title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                                >
                                    {isFullscreen ? <Minimize /> : <Maximize />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 🔥 Modal épisode suivant - DANS le player pour iOS fullscreen */}
                    {showNextEpisodeModal && nextEpisode && (
                        <NextEpisodeModal
                            nextEpisode={nextEpisode}
                            onPlay={handlePlayNextEpisode}
                            onCancel={handleCancelNextEpisode}
                            autoPlayDelay={10}
                            isFullscreen={isFullscreen}
                        />
                    )}
                </div>
            </div>

            {/* Section Rating & Playlist */}
            <div className="vp-footer">
                <div className="vp-footer-inner">
                    <MovieRating movieId={movie.id} user={user} />
                    <div className="vp-footer-row">
                        <AddToPlaylistButton movie={movie} variant="button" />
                    </div>
                </div>
            </div>

            {/* Modal de signalement */}
            {showReportModal && (
                <ReportIssueModal
                    movie={movie}
                    currentTime={currentTime}
                    onClose={() => setShowReportModal(false)}
                />
            )}
        </>
    );
};

export default VideoPlayer;